import { createSlice, PayloadAction } from '@reduxjs/toolkit'

interface SettingsState {
  darkMode: boolean
  sidebarCollapsed: boolean
  sidebarOpenMobile: boolean
  notifications: {
    enabled: boolean
    sound: boolean
    desktop: boolean
    emailAlerts: boolean
  }
  monitoring: {
    autoRefresh: boolean
    refreshInterval: number // in seconds
    showMetrics: boolean
    showAlerts: boolean
  }
  display: {
    compactMode: boolean
    showTimestamps: boolean
    dateFormat: string
    timeZone: string
  }
}

const initialState: SettingsState = {
  darkMode: localStorage.getItem('darkMode') === 'true' || false,
  sidebarCollapsed: localStorage.getItem('sidebarCollapsed') === 'true' || false,
  sidebarOpenMobile: false, // Always start closed on mobile
  notifications: {
    enabled: true,
    sound: true,
    desktop: false,
    emailAlerts: false,
  },
  monitoring: {
    autoRefresh: true,
    refreshInterval: 30, // 30 seconds
    showMetrics: true,
    showAlerts: true,
  },
  display: {
    compactMode: false,
    showTimestamps: true,
    dateFormat: 'YYYY-MM-DD',
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  },
}

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    toggleDarkMode: (state) => {
      state.darkMode = !state.darkMode
      localStorage.setItem('darkMode', state.darkMode.toString())
    },
    setDarkMode: (state, action: PayloadAction<boolean>) => {
      state.darkMode = action.payload
      localStorage.setItem('darkMode', action.payload.toString())
    },
    toggleSidebar: (state) => {
      state.sidebarCollapsed = !state.sidebarCollapsed
      localStorage.setItem('sidebarCollapsed', state.sidebarCollapsed.toString())
    },
    setSidebarCollapsed: (state, action: PayloadAction<boolean>) => {
      state.sidebarCollapsed = action.payload
      localStorage.setItem('sidebarCollapsed', action.payload.toString())
    },
    openMobileSidebar: (state) => {
      state.sidebarOpenMobile = true
    },
    closeMobileSidebar: (state) => {
      state.sidebarOpenMobile = false
    },
    toggleMobileSidebar: (state) => {
      state.sidebarOpenMobile = !state.sidebarOpenMobile
    },
    updateNotificationSettings: (state, action: PayloadAction<Partial<SettingsState['notifications']>>) => {
      state.notifications = { ...state.notifications, ...action.payload }
      localStorage.setItem('notifications', JSON.stringify(state.notifications))
    },
    updateMonitoringSettings: (state, action: PayloadAction<Partial<SettingsState['monitoring']>>) => {
      state.monitoring = { ...state.monitoring, ...action.payload }
      localStorage.setItem('monitoring', JSON.stringify(state.monitoring))
    },
    updateDisplaySettings: (state, action: PayloadAction<Partial<SettingsState['display']>>) => {
      state.display = { ...state.display, ...action.payload }
      localStorage.setItem('display', JSON.stringify(state.display))
    },
    loadSettingsFromStorage: (state) => {
      // Load saved settings from localStorage
      const savedNotifications = localStorage.getItem('notifications')
      const savedMonitoring = localStorage.getItem('monitoring')
      const savedDisplay = localStorage.getItem('display')
      
      if (savedNotifications) {
        try {
          state.notifications = JSON.parse(savedNotifications)
        } catch (e) {
          console.error('Failed to parse saved notifications settings')
        }
      }
      
      if (savedMonitoring) {
        try {
          state.monitoring = JSON.parse(savedMonitoring)
        } catch (e) {
          console.error('Failed to parse saved monitoring settings')
        }
      }
      
      if (savedDisplay) {
        try {
          state.display = JSON.parse(savedDisplay)
        } catch (e) {
          console.error('Failed to parse saved display settings')
        }
      }
    },
    resetSettings: () => {
      // Clear localStorage
      localStorage.removeItem('darkMode')
      localStorage.removeItem('sidebarCollapsed')
      localStorage.removeItem('notifications')
      localStorage.removeItem('monitoring')
      localStorage.removeItem('display')
      
      // Reset to initial state
      return initialState
    },
  },
})

export const {
  toggleDarkMode,
  setDarkMode,
  toggleSidebar,
  setSidebarCollapsed,
  openMobileSidebar,
  closeMobileSidebar,
  toggleMobileSidebar,
  updateNotificationSettings,
  updateMonitoringSettings,
  updateDisplaySettings,
  loadSettingsFromStorage,
  resetSettings,
} = settingsSlice.actions

export default settingsSlice.reducer